import { useEffect } from 'react'
import { Layout, message } from 'antd'
import { useTranslation } from 'react-i18next'
import LoginPage from './login'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom'

import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  //sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth'
import Action from './action'
import SignUpPage from './signup'
import { useAuth, useSigninCheck, useFunctions } from 'reactfire'
import SignedIn from './signedIn'
import styles from '../less/BaseLayout.module.less'
import { showSignUp } from '../lib/showSignUp'
import { api } from '../lib/api'
import { errorResponse } from '../lib/errorResponse'
import { normalizeEmail } from '../lib/shared/normalizeEmail'

export default function Authentication() {
  const auth = useAuth()
  const functions = useFunctions()
  const { data: signInCheck } = useSigninCheck()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()

  async function googleSignup() {
    const provider = new GoogleAuthProvider()
    try {
      return await signInWithPopup(auth, provider)
    } catch (error) {
      const fallbackCodes = new Set([
        'auth/popup-blocked',
        'auth/operation-not-supported-in-this-environment',
        'auth/cancelled-popup-request',
        'auth/internal-error',
      ])
      if (fallbackCodes.has(error?.code)) {
        await signInWithRedirect(auth, provider)
        return
      }
      throw error
    }
  }

  async function emailSignup(email, password) {
    return await createUserWithEmailAndPassword(
      auth,
      normalizeEmail(email),
      password
    )
  }

  async function emailLogin(email, password) {
    try {
      return await signInWithEmailAndPassword(auth, email, password)
    } catch (error) {
      // Accounts are stored under the normalized address since CL-794, so a
      // user typing e.g. @googlemail.com misses their @gmail.com account.
      // Only retry when the raw attempt found no account — a wrong-password
      // error means the raw-form account exists and must stay authoritative
      // (both variants can exist as separate accounts).
      const retryCodes = new Set([
        'auth/user-not-found',
        'auth/invalid-credential',
        'auth/invalid-login-credentials',
        'auth/invalid-email',
      ])
      const normalizedEmail = normalizeEmail(email)
      if (normalizedEmail !== email && retryCodes.has(error?.code)) {
        return await signInWithEmailAndPassword(auth, normalizedEmail, password)
      }
      throw error
    }
  }

  async function sendResetPassword(email) {
    return await api(functions, 'users/reset_password', {
      email: email,
    })
    //return await sendPasswordResetEmail(auth, email)
  }

  useEffect(() => {
    if (window.location.hash === '#emailVerified') {
      message.success(t('frontend:auth.emailVerified'))
      window.location.hash = ''
    }
    if (window.location.hash === '#emailRecovered') {
      message.success(t('frontend:auth.emailRecovered'))
      window.location.hash = ''
    }
    if (window.location.hash === '#mfaReverted') {
      message.success(t('frontend:auth.mfaReverted'))
      window.location.hash = ''
    }
    // Process redirect sign-in result to surface meaningful errors
    getRedirectResult(auth)
      .then(() => {
        // no-op on success; auth state listener will redirect
      })
      .catch((error) => {
        switch (error?.code) {
          case 'auth/unauthorized-domain':
            message.error(t('frontend:auth.domainNotAuthorized'))
            break
          case 'auth/auth-domain-config-required':
            message.error(t('frontend:auth.authDomainConfigMissing'))
            break
          case 'auth/invalid-api-key':
            message.error(t('frontend:auth.invalidApiKey'))
            break
          case 'auth/invalid-credential':
            message.error(t('frontend:auth.invalidCredential'))
            break
          case 'auth/invalid-authorization-code':
            message.error(t('frontend:auth.invalidAuthCode'))
            break
          case 'auth/argument-error':
            message.error(t('frontend:auth.invalidLoginParams'))
            break
          case 'auth/network-request-failed':
            message.error(t('frontend:auth.networkError'))
            break
          case 'auth/invalid-tenant-id':
            message.error(t('frontend:auth.invalidTenantId'))
            break
          case 'auth/operation-not-allowed':
            message.error(t('frontend:auth.googleSigninDisabled'))
            break
          case 'auth/web-storage-unsupported':
            message.error(t('frontend:auth.webStorageBlocked'))
            break
          case 'auth/operation-not-supported-in-this-environment':
            message.error(t('frontend:auth.signinNotSupported'))
            break
          default:
            /* eslint-disable-next-line no-console */
            console.error('Google redirect sign-in error', {
              code: error?.code,
              message: error?.message,
              error,
            })
            errorResponse(
              error,
              t('frontend:auth.loginFailed', {
                code: error?.code || t('frontend:common.unknown'),
              })
            )
            break
        }
      })
  }, [auth, t])

  const location = useLocation()

  // Helper function to safely get post-login destination URL
  // Validates returnUrl to prevent XSS (javascript:) and open redirects
  function getPostLoginDestination() {
    const returnUrl = searchParams.get('returnUrl')
    if (returnUrl) {
      try {
        const decoded = decodeURIComponent(returnUrl)

        // Only allow relative paths starting with single /
        // Reject: //, /\, javascript:, https://, data:, etc.
        if (
          decoded.startsWith('/') &&
          !decoded.startsWith('//') &&
          !/^\/[\\]/.test(decoded)
        ) {
          // Use URL parsing to ensure same-origin
          const url = new URL(decoded, window.location.origin)
          if (url.origin === window.location.origin) {
            return url.pathname + url.search + url.hash
          }
        }

        // Invalid returnUrl, fall back to root
        return '/'
      } catch {
        // If returnUrl is malformed, fall back to root
        return '/'
      }
    }
    // Fall back to root with any existing search params
    return `/${location.search}`
  }

  return (
    <Layout className={styles.baseLayout}>
      <Routes>
        <Route path="/action" element={<Action />} />
        <Route
          path="/login"
          element={
            <>
              {signInCheck.signedIn ? (
                <Navigate to={getPostLoginDestination()} />
              ) : (
                <LoginPage
                  googleSignup={googleSignup}
                  emailLogin={emailLogin}
                  sendResetPassword={sendResetPassword}
                />
              )}
            </>
          }
        />
        <Route
          path="/signup"
          element={
            <>
              {signInCheck.signedIn ? (
                <Navigate to={getPostLoginDestination()} />
              ) : (
                <SignUpPage
                  googleSignup={googleSignup}
                  emailSignup={emailSignup}
                />
              )}
            </>
          }
        />
        <Route
          path="/*"
          element={
            <>
              {!signInCheck.signedIn ? (
                showSignUp(searchParams) ? (
                  <Navigate
                    to={`/signup?returnUrl=${encodeURIComponent(
                      location.pathname + location.search
                    )}`}
                  />
                ) : (
                  <Navigate
                    to={`/login?returnUrl=${encodeURIComponent(
                      location.pathname + location.search
                    )}`}
                  />
                )
              ) : (
                <SignedIn />
              )}
            </>
          }
        />
      </Routes>
    </Layout>
  )
}
